import { useEffect, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useStaffRole } from "@/hooks/useStaffRole";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { PortalMessagesProvider } from "./PortalMessages";
import AccountFlagNotice from "./AccountFlagNotice";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import { supabase } from "@/integrations/supabase/client";

// user_types that may NOT start/continue an application (staff-assigned only).
const BLOCKED_WIZARD_TYPES = ["spam", "vendor", "agent_external"];

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

function isGhostMode(): boolean {
  if (typeof window === "undefined") return false;
  return window.location.search.includes("ghost=1") || sessionStorage.getItem("te_ghost_mode") === "1";
}

/**
 * Routes un-qualified registrants to the first-login survey:
 *  - requires one answer before any other portal page (the survey has no "later";
 *    people who registered by mistake can delete the account from it instead),
 *  - blocks disqualified user_types (spam/vendor/agent_external) from the wizard.
 * Staff and ghost-mode sessions bypass the gate entirely.
 */
function OnboardingGate({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isAdmin, loading: adminLoading } = useAdminRole();
  const { role: staffRole, isPartner, loading: staffLoading } = useStaffRole();
  const { data: status, isLoading: statusLoading } = useOnboardingStatus();

  // Never gate the survey page itself, and never gate ghost-mode previews.
  if (location.pathname.includes("/portal/onboarding") || isGhostMode()) return <>{children}</>;

  // Wait for roles + status so we don't bounce staff or flash the wizard.
  if (adminLoading || staffLoading || statusLoading) return <Spinner />;

  const isStaff = isAdmin || !!staffRole;
  if (isStaff) return <>{children}</>;

  // Approved partners never apply for themselves, so the applicant survey doesn't
  // apply to them. Let them through — Dashboard redirects them on to /partner.
  // (isPartner rides along on the user_roles query useStaffRole already runs, so
  // the applicant path pays for no extra request.)
  if (isPartner) return <>{children}</>;

  const isWizard = /\/portal\/apply\//.test(location.pathname);
  const userType = status?.userType ?? null;

  // Partner leads (partner sign-up, or the survey's partner answer) are not clients:
  // keep them out of the client portal. PartnerRoute shows onboarding or pending.
  if (userType === "partner_lead") return <Navigate to="/partner" replace />;

  if (isWizard && BLOCKED_WIZARD_TYPES.includes(userType ?? "")) {
    return <Navigate to="/portal/dashboard" replace />;
  }

  const needsSurvey = status ? !status.surveyDone && !status.hasApplication : false;
  if (!needsSurvey) return <>{children}</>;
  return <Navigate to="/portal/onboarding" replace />;
}

/**
 * Records a portal visit (profiles.last_engaged_at) once per browser session so
 * returning registrants leave the pipeline's Inactive column. Never for ghost
 * sessions: a staff preview is not customer engagement.
 */
function useTouchEngagement(userId: string | undefined) {
  useEffect(() => {
    if (!userId || isGhostMode()) return;
    const key = `te_engaged_${userId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { /* storage blocked: still record */ }
    (supabase.rpc as any)("touch_engagement").then(undefined, () => {});
  }, [userId]);
}

const ProtectedRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  useTouchEngagement(user?.id);

  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/portal/login" replace />;

  return (
    <AppErrorBoundary context="portal" resetKey={location.pathname + location.search}>
      <OnboardingGate>
        <PortalMessagesProvider key={user.id}><AccountFlagNotice /><Outlet /></PortalMessagesProvider>
      </OnboardingGate>
    </AppErrorBoundary>
  );
};

export default ProtectedRoute;
